
























































import Vue from 'vue'

import {
  IChronologyItem,
  IChronologyItemTypes,
} from '@/types/data-objects/item-histories'

import ChronologyAction from './components/chronology-action.vue'
import ChronologyReply from './components/chronology-reply.vue'
import ChronologyComment from './components/chronology-comment.vue'

export default Vue.extend({
  name: 'Chronology',

  components: {
    ChronologyAction,
    ChronologyComment,
    ChronologyReply,
  },

  props: {
    items: {
      type: Array as () => IChronologyItem[],
      default: (): IChronologyItem[] => [],
    },

    visibilityNote: {
      type: String,
      default: '',
      required: false,
    },

    minInitialItemCountForCollapse: {
      type: Number,
      default: 4,
    },

    commentSubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    incomingReplySubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    isForTask: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      types: IChronologyItemTypes,
      isCollapsed: false,
      initialExposedItemIDs: [] as number[],
      initialHiddenItemCount: 0,
      initialMostRecentCreatedOnEpochTime: 0,
      mousedOverItemID: null,
    }
  },

  computed: {
    exposedItems(): IChronologyItem[] {
      if (!this.isCollapsed) {
        return this.items
      }

      // First, grab items that are initially exposed
      const result = this.items.filter(i => {
        return this.initialExposedItemIDs.includes(i.data.id)
      })

      // Then, add any newer items since the initial data fetch.
      // (Comments, status updates, etc.)
      const newerItems = this.items.filter(i => {
        return (
          i.data.createdOnEpochTime > this.initialMostRecentCreatedOnEpochTime
        )
      })

      if (newerItems.length > 0) {
        return result.concat(newerItems)
      }
      return result
    },

    selectedEvent(): string {
      return this.$route.hash.replace('#', '')
    },

    firstHistoryIsReply(): Boolean {
      return (
        this.items &&
        this.items.length > 0 &&
        (this.items[0].type === IChronologyItemTypes.REPLY_FROM_OUTSIDE_USER ||
          this.items[0].type === IChronologyItemTypes.REPLY_TO_CUSTOMER)
      )
    },
  },

  watch: {
    items: {
      immediate: true,
      handler(newVal: IChronologyItem[], oldVal: IChronologyItem[]) {
        const initialData = oldVal && oldVal.length === 0
        if (initialData) {
          const hasRouteHash: boolean = this.$route.hash ? true : false
          this.setInitialItemsDisplay(this.items, hasRouteHash)
          if (hasRouteHash) {
            this.scrollToItem(this.$route.hash)
          }
        }
      },
    },
  },

  methods: {
    handleMouseEnter(id: number, index: number) {
      /* Don't let first history item to react in collapsed mode */
      if (this.isCollapsed && index === 0) {
        return
      }
      this.mousedOverItemID = id
    },
    handleMouseLeave(id: number, index: number) {
      /* Don't let first history item to react in collapsed mode */
      if (this.isCollapsed && index === 0) {
        return
      }
      this.mousedOverItemID = null
    },
    getClassSuffix(item: IChronologyItem) {
      if (this.isReply(item)) {
        return 'reply'
      } else if (this.isForTask && item.type === IChronologyItemTypes.COMMENT) {
        return 'task-comment'
      }
      return item.type
    },
    isReply(item: IChronologyItem) {
      return (
        item.type === IChronologyItemTypes.REPLY_FROM_OUTSIDE_USER ||
        item.type === IChronologyItemTypes.REPLY_TO_CUSTOMER
      )
    },
    setInitialItemsDisplay(
      initialItems: IChronologyItem[],
      hasRouteHash: boolean,
    ) {
      this.initialExposedItemIDs = initialItems.map(i => i.data.id)
      this.isCollapsed = false
      this.initialMostRecentCreatedOnEpochTime = Math.max.apply(
        Math,
        initialItems.map(i => {
          return i.data.createdOnEpochTime
        }),
      )

      // If there aren't many items or we're anchoring to a comment just show all of them!
      if (
        initialItems.length < this.minInitialItemCountForCollapse ||
        hasRouteHash
      ) {
        return
      }

      const unviewedItemIDs = initialItems
        .filter(i => {
          return !i.data.hasViewed
        })
        .map(i => i.data.id)

      // Always show first and last item and any unviewed
      // We can safely assume unviewed items will appear together and at the bottom of the list
      this.initialExposedItemIDs = this.initialExposedItemIDs.filter(
        (itemID, idx) => {
          const isFirst = idx === 0
          const isLast = idx === initialItems.length - 1
          const isUnviewed = unviewedItemIDs.includes(itemID)

          return isFirst || isLast || isUnviewed
        },
      )

      // Set collapsed state if any items are hidden
      if (initialItems.length > this.initialExposedItemIDs.length) {
        // Adding 1 for the first item which is partially exposed in the UI but considered "hidden"
        this.initialHiddenItemCount =
          initialItems.length - this.initialExposedItemIDs.length + 1
        this.isCollapsed = true
      }
    },

    async scrollToItem(id: string) {
      await this.$nextTick()
      const container = this.$el.querySelector(id)
      if (container) {
        container.scrollIntoView()
        window.scrollBy(0, -120)
      }
    },

    showAllItems() {
      this.isCollapsed = false
    },
  },
})
